import React, { useContext } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import SettingsForm from "@nualang/nualang-ui-components/dist/Forms/Settings";
import {
  users as userQuerys,
  roleplays as roleplaysQuerys,
  bots as botsQuerys,
  courses as coursesQuery,
  classrooms as classroomQuery,
  groups as groupsQuery,
} from "@nualang/nualang-api-and-queries/Queries";
import { ColorModeContext } from "../../themes/ThemeProviders/ColorModeContext";
import { UploadUserImage } from "../../utils/imageUpload";
import { createCustomerPortal } from "@nualang/nualang-api-and-queries/APIs/Payments";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  getTeacherClassroomsAISettings,
  updateClassroom,
} from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { deleteGroupMember } from "@nualang/nualang-api-and-queries/APIs/GroupMembers";
import Loading from "../../components/Misc/Loading";
import { users as userHelpers } from "../../utils/helpers";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { useApp, useSubscription } from "../../hooks";

// config
import config from "../../config";

import logger from "../../logger";

const { languages, forLanguages } = config;

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2, 3),
  },
}));

function Settings(props) {
  const { verificationStatus } = props;
  const { t } = useTranslation();
  const [confirm] = useConfirm(t);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const colorMode = useContext(ColorModeContext);
  const {
    user,
    handleOpenModerateDialog,
    openSnackbar,
    startLoading,
    stopLoading,
    setUser,
  } = useApp();

  const { subscription, cancelSubscription } = useSubscription();

  const { username, attributes } = user;

  const generalInitialValues = {
    email: attributes.email,
    preferred_username: attributes.preferred_username,
    given_name: attributes.given_name || "",
    family_name: attributes.family_name || "",
    "custom:forLang": attributes["custom:forLang"] || "",
    "custom:learnLang": attributes["custom:learnLang"] || "",
    "custom:role": attributes["custom:role"] || "",
    picture: attributes.picture || "",
  };

  const isTeacher = generalInitialValues["custom:role"] === "teach";

  // The AI settings tab: every classroom this teacher created or co-teaches.
  const classroomAIQuery = classroomQuery.useTeacherClassroomsAISettings(
    getTeacherClassroomsAISettings,
    { username },
    { enabled: !!username && isTeacher },
  );
  const updateClassroomAIMutation =
    classroomQuery.useUpdateClassroomAISettings(updateClassroom);

  const classroomAISettings = {
    classrooms: [...(classroomAIQuery.data?.Items || [])].sort((a, b) =>
      (a.classroomName || "").localeCompare(b.classroomName || ""),
    ),
    isLoading: classroomAIQuery.isLoading,
    isError: classroomAIQuery.isError,
    onSave: (classroomId, values) =>
      updateClassroomAIMutation.mutateAsync({ classroomId, values, username }),
    onOpenClassroom: (classroomId) =>
      navigate(`/classrooms/${classroomId}#Settings`),
  };

  // Mutations
  const updateUserMutation = userQuerys.useUpdateUser(
    userHelpers.updateUserAttributes,
    {
      onSettled: () => {
        // Error or success... doesn't matter!
        stopLoading();
      },
      onSuccess: (data) => {
        setUser(data);
        const filters = {
          createdBy: username,
          limit: config.limits.roleplays,
        };
        const courseAndClassroomFilter = {
          createdBy: username,
          collaborator: attributes.email || "",
          verbose: false,
          limit: config.limits.courses,
        };
        openSnackbar("user_updated", "success");
        queryClient.invalidateQueries({
          queryKey: userQuerys.userKeys.item(username),
        });
        const dataTeachRoleplays = queryClient.getQueryData(
          roleplaysQuerys.roleplayKeys.list(filters),
        );
        const dataLearnRoleplaysInProgress = queryClient.getQueryData([
          "roleplays",
          "inProgress",
          username,
        ]);
        const dataLearnRoleplaysCompleted = queryClient.getQueryData([
          "roleplays",
          "completed",
          username,
        ]);

        const dataTeachBots = queryClient.getQueryData(
          botsQuerys.botKeys.list(filters),
        );
        const dataLearnBotsInProgress = queryClient.getQueryData([
          "bots",
          "inProgress",
          username,
        ]);
        const dataLearnBotsCompleted = queryClient.getQueryData([
          "bots",
          "completed",
          username,
        ]);

        const dataTeachCourses = queryClient.getQueryData(
          coursesQuery.courseKeys.list(courseAndClassroomFilter),
        );
        const dataTeachClassrooms = queryClient.getQueryData(
          classroomQuery.classroomKeys.list(courseAndClassroomFilter),
        );

        if (dataTeachCourses) {
          queryClient.setQueryData(
            coursesQuery.courseKeys.list(courseAndClassroomFilter),
            (old) => {
              return old.map((item) => {
                if (item.createdBy === username) {
                  return {
                    ...item,
                    createdByName: data.attributes.preferred_username,
                    userImage: data.attributes.picture,
                  };
                } else {
                  return item;
                }
              });
            },
          );
        }

        if (dataTeachClassrooms) {
          queryClient.setQueryData(
            classroomQuery.classroomKeys.list(courseAndClassroomFilter),
            (old) => {
              return old.map((item) => {
                if (item.createdBy === username) {
                  return {
                    ...item,
                    createdByName: data.attributes.preferred_username,
                    userImage: data.attributes.picture,
                  };
                } else {
                  return item;
                }
              });
            },
          );
        }

        if (dataTeachRoleplays) {
          queryClient.setQueryData(
            roleplaysQuerys.roleplayKeys.list(filters),
            (old) => {
              return old.map((item) => {
                if (item.createdBy === username) {
                  return {
                    ...item,
                    createdByName: data.attributes.preferred_username,
                    userImage: data.attributes.picture,
                  };
                } else {
                  return item;
                }
              });
            },
          );
        }
        if (dataLearnRoleplaysInProgress) {
          queryClient.setQueryData(
            ["roleplays", "inProgress", username],
            (old) => {
              return old.map((item) => {
                if (item.createdBy === username) {
                  return {
                    ...item,
                    createdByName: data.attributes.preferred_username,
                    userImage: data.attributes.picture,
                  };
                } else {
                  return item;
                }
              });
            },
          );
        }
        if (dataLearnRoleplaysCompleted) {
          queryClient.setQueryData(
            ["roleplays", "completed", username],
            (old) => {
              return old.map((item) => {
                if (item.createdBy === username) {
                  return {
                    ...item,
                    createdByName: data.attributes.preferred_username,
                    userImage: data.attributes.picture,
                  };
                } else {
                  return item;
                }
              });
            },
          );
        }
        if (dataTeachBots) {
          queryClient.setQueryData(botsQuerys.botKeys.list(filters), (old) => {
            return old.map((item) => {
              if (item.createdBy === username) {
                return {
                  ...item,
                  createdByName: data.attributes.preferred_username,
                  userImage: data.attributes.picture,
                };
              } else {
                return item;
              }
            });
          });
        }
        if (dataLearnBotsInProgress) {
          queryClient.setQueryData(["bots", "inProgress", username], (old) => {
            return old.map((item) => {
              if (item.createdBy === username) {
                return {
                  ...item,
                  createdByName: data.attributes.preferred_username,
                  userImage: data.attributes.picture,
                };
              } else {
                return item;
              }
            });
          });
        }
        if (dataLearnBotsCompleted) {
          queryClient.setQueryData(["bots", "completed", username], (old) => {
            return old.map((item) => {
              if (item.createdBy === username) {
                return {
                  ...item,
                  createdByName: data.attributes.preferred_username,
                  userImage: data.attributes.picture,
                };
              } else {
                return item;
              }
            });
          });
        }
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const handleUpdateUser = (values) => {
    startLoading();
    const { "custom:learnLang": learnLanguage, "custom:forLang": forLanguage } =
      values;
    if (
      (learnLanguage && !languages.hasOwnProperty(learnLanguage)) ||
      (forLanguage && !forLanguages.hasOwnProperty(forLanguage))
    ) {
      openSnackbar("invalid values submitted", "error");
    } else {
      updateUserMutation.mutate({ userId: username, values });
    }
  };
  const handleManagePlan = async () => {
    try {
      if (subscription.stripeCustomerId) {
        const portalParams = {
          domainURL: window.location.href,
          customerId: subscription.stripeCustomerId,
        };
        const response = await createCustomerPortal(portalParams);
        const { portalSession } = response;
        if (portalSession && portalSession.url) {
          window.location.href = portalSession.url;
        }
      } else {
        window.location.href = `${config.SITE_URL}/pricing/${subscription && subscription.plan && subscription.plan !== "basic" ? "upgrade" : "app"}`;
      }
    } catch (error) {
      logger.error(error);
    }
  };

  

  const leaveGroupMutation = groupsQuery.useLeaveGroup(
    deleteGroupMember,
    {
      onMutate: () => {
        startLoading();
      },
      onSettled: () => {
        stopLoading();
      },
      onError: () => {
        openSnackbar("problem", "error");
      }
    }
  )

  const handleLeaveGroup = async (groupId) => {
    await leaveGroupMutation.mutateAsync({groupId, username});
  };

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, false);
      return response;
    },
    {
      userId: username,
    },
    {
      enabled: !!username,
    },
  );
  const currentUser =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const defaultNotificationSettings = {
    student_completes_exercise: false,
    student_reports_problem: true,
    student_acts_inappropriately: true,
  };

  const emailNotificationOnSubmit = (checkBoxState) => {
    const value = {
      email_notification: checkBoxState,
    };
    handleUpdateUser(value);
  };

  const saveAvatarToDynamo = (avatarAttributes) => {
    const value = {
      avatar: avatarAttributes,
    };
    handleUpdateUser(value);
  };

  return (
    <Root>
      {subscription && subscription.isSubscriptionLoaded ? (
        <SettingsForm
          t={t}
          enableReinitialize={true}
          languages={languages}
          forLanguages={forLanguages}
          generalInitialValues={generalInitialValues}
          generalOnSubmit={async (values, { setSubmitting }) => {
            handleUpdateUser(values);
            setSubmitting(false);
          }}
          toggleTheme={colorMode.toggleColorMode}
          subscription={subscription}
          cancelSubscription={cancelSubscription}
          groupSubscriptions={subscription.groupSubscriptions}
          handleManagePlan={handleManagePlan}
          managePlanButtonText={
            subscription.stripeCustomerId ? t("manage_plan") : t("upgrade_plan")
          }
          handleLeaveGroup={handleLeaveGroup}
          leaveGroupButtonText={t("leave_group")}
          isCreator={isTeacher}
          classroomAISettings={classroomAISettings}
          userEmailNotificationSettingsObject={
            currentUser.UserAttributes.email_notification
              ? currentUser.UserAttributes.email_notification
              : defaultNotificationSettings
          }
          emailNotificationOnSubmit={emailNotificationOnSubmit}
          initialAvatarAttributes={
            currentUser.UserAttributes.avatar
              ? currentUser.UserAttributes.avatar
              : {}
          }
          saveAvatarToDynamo={saveAvatarToDynamo}
          username={username}
          verificationStatus={
            currentUser.verificationStatus ? currentUser.verificationStatus : ""
          }
          gameAward={currentUser.gameAward ? currentUser.gameAward : ""}
          EmailNotificationEvents={[
            { name: "student_completes_exercise" },
            { name: "student_reports_problem" },
            { name: "student_acts_inappropriately" },
          ]}
          submitAvatar={async (values, { setSubmitting }) => {
            if (values.picture && values.picture !== attributes.picture) {
              values.pictureWarnDetails = {};
              const base64String = values.picture.split(",");
              const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
                await UploadUserImage(
                  base64String[1] || base64String[0],
                  username,
                );
              if (moderated || warn) {
                if (verificationStatus === "approved" || warn) {
                  const confirmation = await confirm(
                    `${t("warning")}`,
                    `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
                    `${t("use_anyways")}`,
                  );
                  if (confirmation !== true) {
                    return;
                  }
                  values.pictureWarnDetails = {
                    user: username,
                    labels: moderatedLabels.join(", "),
                  };
                } else {
                  const moderationReason = moderatedLabels[0];
                  handleOpenModerateDialog(moderationReason);
                  return;
                }
              }
              if (picture && !moderated) {
                values.picture = picture;
                values.pictureS3Key = pictureS3Key;
              } else {
                delete values.picture;
              }
            }
            handleUpdateUser(values);
            setSubmitting(false);
          }}
        />
      ) : (
        <Loading />
      )}
    </Root>
  );
}

Settings.propTypes = {
  themeContext: PropTypes.object,
};

export default Settings;
